import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Play, Square, RotateCcw, Copy, Check, RefreshCw, AlertCircle, FileText, WifiOff, CalendarClock, Sparkles, LayoutDashboard, Mail, Send, Loader2 } from "lucide-react";
import { AgentSwarmPanel } from "./AgentSwarmPanel";
import { ThreatPanel } from "./ThreatPanel";
import { Battlecard } from "./Battlecard";
import { ExportBar } from "./ExportBar";
import { MarkdownView } from "./MarkdownView";
import { Sources } from "./Sources";
import { GatheringSignals } from "./GatheringSignals";
import { AlertsToggle } from "./AlertsToggle";
import { useSwarmStream } from "@/hooks/useSwarmStream";
import { getProfile, type NovaSynthProfile } from "@/lib/profile";
import { relativeTime, relativeFuture } from "@/lib/time";
import { fetchUsageRemote, emailBriefRemote, slackBriefRemote, type Viewer, type UsageInfo } from "@/lib/agentcore";
import { isValidSlackWebhook } from "./SlackConnect";

type SignalKey = "video" | "document" | "web" | "news";

interface CompetitorAnalysisProps {
  idToken: string;
  sessionId: string;
  onAuthError: () => void;
  initialCompetitor?: string;
  onInitialCompetitorConsumed?: () => void;
  /**
   * Current account profile - hydrated from the account-tied store on login and
   * kept in App state. Passed in (rather than read once from localStorage) so the
   * tracked-competitor chips stay reactive to the loaded/updated profile. Falls
   * back to the localStorage cache for local-dev when the server store is absent.
   */
  profile?: NovaSynthProfile | null;
}

function formatElapsed(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

/** A short wall-clock time for a reset timestamp, e.g. "9:00 AM". Empty when unknown. */
function resetClock(iso: string | undefined | null): string {
  if (!iso) return "";
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return "";
  try {
    return new Date(t).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  } catch {
    return "";
  }
}

export function CompetitorAnalysis({ idToken, sessionId, onAuthError, initialCompetitor, onInitialCompetitorConsumed, profile }: CompetitorAnalysisProps) {
  const [competitorInput, setCompetitorInput] = useState("");
  // Prefer the reactive account profile passed from App (loaded from the
  // account-tied store on login + updated on save). Fall back to the
  // localStorage cache only when the prop is absent (local dev / first paint).
  const savedProfile = profile ?? getProfile();
  const savedCompetitors = savedProfile?.competitors ?? [];
  // Viewer context personalises the threat assessment ("threat to US, and why").
  // The backend tolerates an empty {} when the profile isn't loaded yet.
  const viewer: Viewer = savedProfile
    ? {
        companyName: savedProfile.companyName,
        industry: savedProfile.industry,
        companySize: savedProfile.companySize,
        role: savedProfile.role,
        competitors: savedProfile.competitors,
        // Optional richer personalization (Task F) - forwarded only when present;
        // the backend persona scorer tolerates missing/extra keys.
        ...(savedProfile.icp ? { icp: savedProfile.icp } : {}),
        ...(savedProfile.valueProps ? { valueProps: savedProfile.valueProps } : {}),
        ...(savedProfile.differentiators ? { differentiators: savedProfile.differentiators } : {}),
        ...(savedProfile.winLossNotes ? { winLossNotes: savedProfile.winLossNotes } : {}),
        ...(savedProfile.targetSegments ? { targetSegments: savedProfile.targetSegments } : {}),
      }
    : {};
  const signals: Record<SignalKey, boolean> = {
    video:    savedProfile?.signals?.video    ?? true,
    document: savedProfile?.signals?.document ?? true,
    web:      savedProfile?.signals?.web      ?? true,
    news:     savedProfile?.signals?.news     ?? true,
  };
  const depth = "deep" as const;
  // Caching is silent: every run uses the cached path (forceRefresh: false).
  // We deliberately do NOT expose a force-refresh / cache control to the user.
  const [currentCompetitor, setCurrentCompetitor] = useState("");
  const [liveElapsed, setLiveElapsed] = useState(0);

  const [reportCopied, setReportCopied] = useState(false);

  // ── Deliver-this-brief actions (email / Slack) ──────────────────────────────
  // Subtle on-brand buttons in the output header. Email always available once a
  // brief exists; "Send to Slack" only when a valid webhook is connected.
  const slackConnected = isValidSlackWebhook(savedProfile?.slackWebhook ?? "");
  type DeliverState = "idle" | "sending" | "ok" | "error";
  const [emailState, setEmailState] = useState<DeliverState>("idle");
  const [slackState, setSlackState] = useState<DeliverState>("idle");

  const emailBrief = useCallback(async () => {
    if (!currentCompetitor || emailState === "sending") return;
    setEmailState("sending");
    const res = await emailBriefRemote(idToken, currentCompetitor);
    setEmailState(res.ok ? "ok" : "error");
    setTimeout(() => setEmailState("idle"), 3500);
  }, [currentCompetitor, emailState, idToken]);

  const slackBrief = useCallback(async () => {
    if (!currentCompetitor || slackState === "sending") return;
    setSlackState("sending");
    const res = await slackBriefRemote(idToken, currentCompetitor);
    setSlackState(res.ok ? "ok" : "error");
    setTimeout(() => setSlackState("idle"), 3500);
  }, [currentCompetitor, slackState, idToken]);

  const outputEndRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const swarm = useSwarmStream(idToken, sessionId, onAuthError);
  const { state, connection, recoverable, resume, limitInfo } = swarm;

  // ── Daily allowance indicator ───────────────────────────────────────────────
  // Read the viewer's daily analysis allowance on mount + after each run. Stays
  // null when the feature is unconfigured (local dev): we then show NOTHING and
  // never block - the whole quota UI degrades silently.
  const [usage, setUsage] = useState<UsageInfo | null>(null);
  const refreshUsage = useCallback(async () => {
    const u = await fetchUsageRemote(idToken);
    setUsage(u); // null on failure -> indicator + gating disappear (graceful)
  }, [idToken]);
  useEffect(() => { void refreshUsage(); }, [refreshUsage]);

  const isRunning = state.runStatus === "running";
  const hasOutput = state.synthesisText.length > 0;
  const hasError = state.runStatus === "error";
  // ── Allowance gating (daily AND monthly) ────────────────────────────────────
  // The day chip stays daily-focused, but EITHER window can bind: disable Analyze
  // when the account is paused OR daily remaining is 0 OR monthly remaining is 0.
  // A window only counts when it is KNOWN (usage non-null) AND has a real limit
  // (limit > 0) - an unconfigured / older backend (limit 0) never blocks.
  const dailyBlocked = usage !== null && usage.daily.limit > 0 && usage.daily.remaining <= 0;
  const monthlyBlocked = usage !== null && usage.monthly.limit > 0 && usage.monthly.remaining <= 0;
  const suspended = usage !== null && usage.status === "suspended";
  const outOfAllowance = suspended || dailyBlocked || monthlyBlocked;
  // When both windows are spent, the MONTH is the longer wait, so surface it as the
  // binding constraint in the tooltip / chip; otherwise show whichever is spent.
  const bindingWindow: "month" | "day" = monthlyBlocked ? "month" : "day";
  const canAnalyze = competitorInput.trim().length > 0 && !isRunning && !outOfAllowance;
  // When the current run finished, stamp a freshness time for the brief.
  const completedAtIso = state.runStatus === "done" && state.startedAt && state.elapsedMs !== null
    ? new Date(state.startedAt + state.elapsedMs).toISOString()
    : null;

  type DisplayStatus = "idle" | "running" | "done" | "error";
  const displayStatus: DisplayStatus = state.runStatus as DisplayStatus;

  // ── Non-technical loading -> completion beat -> reveal ──────────────────────
  // The big "Gathering signals" hero shows while the swarm is in flight and the
  // synthesis layer (threat + battlecard) has not yet landed. The moment the run
  // settles we play a short, warm "Got it." beat, THEN reveal the brief/threat/
  // battlecard. "showBeat" is the brief transition window.
  const synthesisReady = state.threat !== null && state.battlecard !== null;
  const showGathering = isRunning && !synthesisReady;
  const [showBeat, setShowBeat] = useState(false);
  const wasGatheringRef = useRef(false);

  useEffect(() => {
    if (showGathering) {
      wasGatheringRef.current = true;
      return;
    }
    // We were gathering and just settled into a real result -> play the beat once.
    if (wasGatheringRef.current && (hasOutput || state.runStatus === "done")) {
      wasGatheringRef.current = false;
      setShowBeat(true);
      const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
      const id = setTimeout(() => setShowBeat(false), reduce ? 250 : 1400);
      return () => clearTimeout(id);
    }
  }, [showGathering, hasOutput, state.runStatus]);

  useEffect(() => {
    if (isRunning && state.startedAt) {
      timerRef.current = setInterval(() => {
        setLiveElapsed(Date.now() - (state.startedAt ?? Date.now()));
      }, 250);
    } else {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [isRunning, state.startedAt]);

  useEffect(() => {
    if (isRunning) {
      outputEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [state.synthesisText, isRunning]);

  // Re-read the allowance the moment a run settles (done / blocked by a limit) so
  // the "N of M left today" indicator + button gating reflect the run just made.
  useEffect(() => {
    if (state.runStatus === "done" || limitInfo) void refreshUsage();
  }, [state.runStatus, limitInfo, refreshUsage]);

  // Auto-start when launched from onboarding / landing / watchlist navigation.
  // Keyed on initialCompetitor so a fresh navigation (e.g. "Run again" for a new
  // competitor) re-triggers even though the component stays mounted on the route.
  useEffect(() => {
    if (initialCompetitor && initialCompetitor.trim() && !isRunning) {
      const c = initialCompetitor.trim();
      setCompetitorInput(c);
      onInitialCompetitorConsumed?.();
      setCurrentCompetitor(c);
      setLiveElapsed(0);
      swarm.start(c, depth, false, viewer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCompetitor]);

  const startAnalysis = useCallback(async () => {
    const trimmed = competitorInput.trim();
    if (!trimmed || isRunning) return;
    setCurrentCompetitor(trimmed);
    setLiveElapsed(0);
    // forceRefresh stays false: caching works silently, never user-controlled.
    await swarm.start(trimmed, depth, false, viewer);
  }, [competitorInput, depth, isRunning, swarm, viewer]);

  const stopAnalysis = useCallback(() => { swarm.stop(); }, [swarm]);

  const resetAnalysis = useCallback(() => {
    swarm.reset();
    setCurrentCompetitor("");
    setCompetitorInput("");
    setLiveElapsed(0);
  }, [swarm]);

  const copyReport = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(state.synthesisText);
      setReportCopied(true);
      setTimeout(() => setReportCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  }, [state.synthesisText]);

  function StatusBadge() {
    switch (displayStatus) {
      case "running":
        return (
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-[#22d3ee] bg-[#22d3ee]/8 px-2.5 py-1 rounded-full border border-[#22d3ee]/25 animate-glitch">
            <span className="relative flex w-1.5 h-1.5 shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#22d3ee] opacity-60" />
              <span className="relative inline-flex rounded-full w-1.5 h-1.5 bg-[#22d3ee]" />
            </span>
            Analysing - {formatElapsed(liveElapsed)}
          </span>
        );
      case "done":
        return (
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-400 bg-emerald-950/30 px-2.5 py-1 rounded-full border border-emerald-800/35">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" />
            Complete {state.elapsedMs !== null && ` - ${formatElapsed(state.elapsedMs)}`}
          </span>
        );
      case "error":
        return (
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-red-400 bg-red-950/30 px-2.5 py-1 rounded-full border border-red-800/35">
            <span className="w-1.5 h-1.5 rounded-full bg-red-400 shrink-0" />
            Error
          </span>
        );
      default:
        return null;
    }
  }

  return (
    <div className="flex flex-col gap-6 w-full">

      {/* ── Section heading above the launch panel ── */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <span className="text-[10px] font-semibold text-[#22d3ee] uppercase tracking-[0.2em]">New analysis</span>
        </div>
        <h2 className="font-mono text-xl sm:text-2xl font-bold tracking-tight text-white">
          Who do you want to track?
        </h2>
        <p className="mt-1.5 text-sm sm:text-base text-slate-400 max-w-2xl leading-relaxed">
          Type a competitor. We scan news, filings, the web, and video, then hand you one
          threat-scored brief and a battlecard - in minutes.
        </p>
      </div>

      {/* ── Input card ── */}
      <div className="ns-glass rounded-2xl p-6 shadow-card">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end">

          {/* Competitor input */}
          <div className="flex-1">
            <label
              htmlFor="competitor-input"
              className="block text-[11px] font-semibold text-slate-400 uppercase tracking-widest mb-2"
            >
              Company or domain
            </label>
            <div className="relative">
              <div className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500">
                <Search className="w-4 h-4" />
              </div>
              <input
                id="competitor-input"
                ref={inputRef}
                type="text"
                value={competitorInput}
                onChange={(e) => setCompetitorInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && canAnalyze) startAnalysis(); }}
                disabled={isRunning}
                placeholder="e.g. salesforce.com or Salesforce"
                className="
                  w-full pl-10 pr-4 py-3 bg-[#070d1a]/80 border border-[#1a2640]
                  hover:border-[#263350] rounded-xl text-slate-100 placeholder-slate-600 text-base sm:text-sm
                  focus:outline-none focus:border-[#22d3ee]/50 focus:ring-2 focus:ring-[#22d3ee]/25
                  disabled:opacity-40 disabled:cursor-not-allowed
                  transition-all duration-200 font-mono min-h-[44px]
                "
              />
            </div>
          </div>

          {/* Action button */}
          <div className="shrink-0 flex flex-col items-stretch sm:items-end">
            {isRunning ? (
              <button
                onClick={stopAnalysis}
                className="
                  w-full sm:w-auto px-5 py-3 bg-red-950/50 hover:bg-red-950/70
                  border border-red-700/40 text-red-300 text-sm font-semibold
                  rounded-xl transition-all duration-200 flex items-center justify-center gap-2 whitespace-nowrap
                  cursor-pointer min-h-[44px]
                "
              >
                <Square className="w-3.5 h-3.5 fill-current" />
                Stop
              </button>
            ) : (
              <button
                onClick={startAnalysis}
                disabled={!canAnalyze}
                title={
                  outOfAllowance
                    ? suspended
                      ? "Your account is paused - reach out and we'll get you running again."
                      : bindingWindow === "month"
                        ? `That is all of this month's analyses - more open up ${
                            relativeFuture(usage?.monthly.resetsAt) || "next month"
                          }.`
                        : `That is all of today's analyses - more open up ${
                            relativeFuture(usage?.daily.resetsAt) || "soon"
                          }${resetClock(usage?.daily.resetsAt) ? ` (at ${resetClock(usage?.daily.resetsAt)})` : ""}.`
                    : undefined
                }
                className="
                  w-full sm:w-auto px-6 py-3
                  bg-gradient-to-r from-brand-600 to-brand-500
                  hover:from-brand-500 hover:to-brand-400
                  disabled:opacity-35 disabled:cursor-not-allowed disabled:hover:from-brand-600 disabled:hover:to-brand-500
                  text-white text-sm font-semibold rounded-xl
                  transition-all duration-200
                  flex items-center justify-center gap-2 whitespace-nowrap
                  shadow-glow-brand-sm hover:shadow-glow-brand
                  focus:outline-none focus:ring-2 focus:ring-brand-500/50 focus:ring-offset-2 focus:ring-offset-[#020617]
                  cursor-pointer min-h-[44px]
                "
              >
                <Play className="w-4 h-4 fill-current" />
                Analyze
              </button>
            )}
          </div>
        </div>

        {savedCompetitors.length > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-1.5">
            <span className="text-[10px] text-slate-600 uppercase tracking-widest font-semibold">Tracking</span>
            {savedCompetitors.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => { setCompetitorInput(c); inputRef.current?.focus(); }}
                disabled={isRunning}
                className="px-2.5 py-1.5 text-[11px] font-mono text-slate-400 bg-white/5 hover:bg-[#22d3ee]/10 hover:text-[#22d3ee] border border-[#1a2640] hover:border-[#22d3ee]/30 rounded-lg transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer min-h-[36px]"
              >
                {c}
              </button>
            ))}
          </div>
        )}
        <div className="mt-3 flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-[11px] text-slate-600 leading-relaxed">
            Five specialists - News, Documents, Web, Video, and Threat Scoring - work in parallel on every brief.
          </p>
          {/* Subtle, on-brand allowance indicator. Daily-focused by default
              ("N of M analyses left today"), but when the MONTH is the binding
              constraint it switches to "N of M left this month" so the user sees the
              real wall. Renders only when an allowance is KNOWN (the feature is
              configured) - otherwise nothing, so local dev / unconfigured backends
              never show or block anything. */}
          {usage && usage.status === "active" && (usage.daily.limit > 0 || usage.monthly.limit > 0) && (
            <span
              className={`inline-flex items-center gap-1.5 self-start rounded-full border px-2.5 py-1 text-[11px] font-medium shrink-0 ${
                outOfAllowance
                  ? "border-amber-600/35 bg-amber-500/5 text-amber-300"
                  : "border-[#1a2640] bg-white/[0.03] text-slate-400"
              }`}
              title={
                monthlyBlocked
                  ? `More analyses open up ${relativeFuture(usage.monthly.resetsAt) || "next month"}.`
                  : dailyBlocked
                    ? `More analyses open up ${relativeFuture(usage.daily.resetsAt) || "soon"}${
                        resetClock(usage.daily.resetsAt) ? ` (at ${resetClock(usage.daily.resetsAt)})` : ""
                      }.`
                    : usage.monthly.limit > 0
                      ? `${usage.daily.used} of ${usage.daily.limit} used today; ${usage.monthly.remaining} of ${usage.monthly.limit} left this month.`
                      : `${usage.daily.used} of ${usage.daily.limit} used today.`
              }
            >
              <Sparkles className="w-3 h-3" />
              {monthlyBlocked
                ? `No analyses left this month - back ${relativeFuture(usage.monthly.resetsAt) || "next month"}`
                : dailyBlocked
                  ? `No analyses left today - back ${relativeFuture(usage.daily.resetsAt) || "tomorrow"}`
                  : usage.monthly.limit > 0 && usage.monthly.remaining < usage.daily.remaining
                    ? `${usage.monthly.remaining} of ${usage.monthly.limit} analyses left this month`
                    : `${usage.daily.remaining} of ${usage.daily.limit} analyses left today`}
            </span>
          )}
        </div>
      </div>

      {/* ── Friendly daily-allowance panel ──
          Shown instead of a scary error when the backend gently stops a run
          because a daily allowance was reached (RATE_LIMITED -> RUN_FINISHED,
          HTTP 200). Calm copy, no "rate limit"/"quota exceeded" jargon, plus a
          clear "Back to watchlist" action. */}
      {limitInfo && (
        <div className="ns-glass rounded-2xl p-6 shadow-card animate-slide-up">
          <div className="flex flex-col sm:flex-row sm:items-start gap-4">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-[#22d3ee]/30 bg-[#22d3ee]/10 text-[#22d3ee] shrink-0">
              {limitInfo.scope === "suspended" ? <AlertCircle className="w-5 h-5" /> : <CalendarClock className="w-5 h-5" />}
            </span>
            <div className="min-w-0 flex-1">
              {limitInfo.scope === "suspended" ? (
                <>
                  <h3 className="text-base font-semibold text-white">Your account is paused</h3>
                  <p className="mt-1.5 text-sm text-slate-400 leading-relaxed">
                    Analyses are on hold for now. Reach out to us and we'll get you back up and running -
                    nothing you have saved is affected.
                  </p>
                </>
              ) : limitInfo.scope === "global" ? (
                <>
                  <h3 className="text-base font-semibold text-white">NovaSynth is at capacity for today</h3>
                  <p className="mt-1.5 text-sm text-slate-400 leading-relaxed">
                    We have reached today's shared analysis capacity. Please check back tomorrow and you
                    will be able to run again. Your saved briefs and watchlist stay available in the meantime.
                  </p>
                </>
              ) : limitInfo.scope === "month" ? (
                <>
                  <h3 className="text-base font-semibold text-white">
                    You have used all{" "}
                    {limitInfo.limit > 0 ? limitInfo.limit : limitInfo.used} analyses for this month
                  </h3>
                  <p className="mt-1.5 text-sm text-slate-400 leading-relaxed">
                    More open up {relativeFuture(limitInfo.resetsAt) || "next month"}. Your saved
                    briefs and watchlist are all still here.
                  </p>
                </>
              ) : (
                <>
                  <h3 className="text-base font-semibold text-white">
                    You have used all{" "}
                    {limitInfo.limit > 0 ? limitInfo.limit : limitInfo.used} of today's analyses
                  </h3>
                  <p className="mt-1.5 text-sm text-slate-400 leading-relaxed">
                    More open up {relativeFuture(limitInfo.resetsAt) || "soon"}
                    {resetClock(limitInfo.resetsAt) ? ` (at ${resetClock(limitInfo.resetsAt)})` : ""}. Your saved
                    briefs and watchlist are all still here in the meantime.
                  </p>
                </>
              )}
              <div className="mt-4 flex flex-wrap items-center gap-2.5">
                <button
                  type="button"
                  onClick={() => navigate("/app")}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-brand-600 to-brand-500 px-4 py-2.5 text-sm font-semibold text-white shadow-glow-brand-sm transition-all duration-200 hover:from-brand-500 hover:to-brand-400 hover:shadow-glow-brand cursor-pointer min-h-[44px]"
                >
                  <LayoutDashboard className="w-4 h-4" />
                  Back to watchlist
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Output card ── */}
      {!limitInfo && (hasOutput || hasError || isRunning) && (
        <div data-print="region" className="bg-[#0d1424]/80 border border-[#1a2640] rounded-2xl shadow-card-lg overflow-hidden animate-slide-up">

          {/* Output header */}
          <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-[#1a2640] bg-[#091020]/50">
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-700/25 border border-brand-600/30 text-brand-300 shrink-0">
                <FileText className="w-4 h-4" />
              </div>
              <div className="min-w-0">
                <h2 className="text-sm font-medium text-slate-400 truncate">
                  {currentCompetitor && (
                    <span className="text-white font-semibold font-mono">{currentCompetitor}</span>
                  )}
                  {" "}
                  <span className="text-slate-500">intelligence brief</span>
                </h2>
                {completedAtIso && (
                  <p className="text-[11px] text-slate-600 mt-0.5">
                    Analyzed {relativeTime(completedAtIso)}
                  </p>
                )}
              </div>
              <StatusBadge />
            </div>

            <div className="flex items-center gap-1.5 shrink-0" data-print="hide">
              {hasOutput && !isRunning && (
                <ExportBar
                  compact
                  payload={{
                    competitor: currentCompetitor,
                    threat: state.threat,
                    synthesisText: state.synthesisText,
                    battlecard: state.battlecard,
                    runAt: completedAtIso ?? undefined,
                  }}
                />
              )}
              {hasOutput && !isRunning && (
                <button
                  onClick={emailBrief}
                  disabled={emailState === "sending"}
                  title="Email this brief to your inbox"
                  className={`inline-flex items-center gap-1.5 px-2.5 py-2 text-xs rounded-lg transition-colors cursor-pointer min-h-[40px] disabled:cursor-not-allowed ${
                    emailState === "ok"
                      ? "text-emerald-400"
                      : emailState === "error"
                        ? "text-amber-400"
                        : "text-slate-400 hover:text-[#22d3ee] hover:bg-white/5"
                  }`}
                >
                  {emailState === "sending" ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : emailState === "ok" ? (
                    <Check className="w-3.5 h-3.5" />
                  ) : (
                    <Mail className="w-3.5 h-3.5" />
                  )}
                  <span className="hidden lg:inline">
                    {emailState === "ok"
                      ? "Sent to your inbox"
                      : emailState === "error"
                        ? "Couldn't send"
                        : "Email me this"}
                  </span>
                </button>
              )}
              {hasOutput && !isRunning && slackConnected && (
                <button
                  onClick={slackBrief}
                  disabled={slackState === "sending"}
                  title="Send this brief to your Slack channel"
                  className={`inline-flex items-center gap-1.5 px-2.5 py-2 text-xs rounded-lg transition-colors cursor-pointer min-h-[40px] disabled:cursor-not-allowed ${
                    slackState === "ok"
                      ? "text-emerald-400"
                      : slackState === "error"
                        ? "text-amber-400"
                        : "text-slate-400 hover:text-[#22d3ee] hover:bg-white/5"
                  }`}
                >
                  {slackState === "sending" ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : slackState === "ok" ? (
                    <Check className="w-3.5 h-3.5" />
                  ) : (
                    <Send className="w-3.5 h-3.5" />
                  )}
                  <span className="hidden lg:inline">
                    {slackState === "ok"
                      ? "Sent to Slack"
                      : slackState === "error"
                        ? "Couldn't send"
                        : "Send to Slack"}
                  </span>
                </button>
              )}
              {hasOutput && !isRunning && (
                <button
                  onClick={copyReport}
                  title="Copy raw brief text"
                  className="inline-flex items-center gap-1.5 px-2.5 py-2 text-xs text-slate-400 hover:text-[#22d3ee] hover:bg-white/5 rounded-lg transition-colors cursor-pointer min-h-[40px]"
                >
                  {reportCopied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                  <span className="hidden lg:inline">{reportCopied ? "Copied" : "Copy text"}</span>
                </button>
              )}
              <button
                onClick={resetAnalysis}
                title="New analysis"
                className="inline-flex items-center gap-1.5 px-2.5 py-2 text-xs text-slate-400 hover:text-slate-100 hover:bg-white/5 rounded-lg transition-colors cursor-pointer min-h-[40px]"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">New</span>
              </button>
            </div>
          </div>

          {/* Calm, non-scary connection banner. Replaces a raw "Network error"
              when a mobile run drops its socket (backgrounded tab / lost signal).
              The run stays in flight; we auto-retry with backoff and offer a
              manual Resume/Retry as the fallback. */}
          {recoverable && (
            <div className="mx-6 mt-5 flex flex-col sm:flex-row sm:items-center gap-3 bg-amber-950/20 border border-amber-700/30 rounded-xl p-4">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg border border-amber-600/40 bg-amber-500/10 text-amber-300 shrink-0">
                {connection === "offline"
                  ? <WifiOff className="w-4 h-4" />
                  : <RefreshCw className="w-4 h-4 animate-spin" />}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-amber-200">
                  {connection === "offline"
                    ? "You went offline - we'll pick back up"
                    : "Lost connection - reconnecting…"}
                </p>
                <p className="text-xs text-amber-300/70 mt-0.5 leading-relaxed">
                  {connection === "offline"
                    ? "Your analysis is safe. We'll resume the moment you're back online."
                    : "This can happen on mobile. We'll grab your results as soon as we reconnect."}
                </p>
              </div>
              <button
                type="button"
                onClick={resume}
                className="shrink-0 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-amber-500/15 hover:bg-amber-500/25 border border-amber-600/40 text-amber-200 text-sm font-semibold transition-colors cursor-pointer min-h-[40px]"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Resume now
              </button>
            </div>
          )}

          {/* Big, friendly, non-technical loading hero. Leads the output while the
              swarm gathers signals, then plays a short warm completion beat. Wired
              to the REAL agent statuses so a newcomer SEES it working + understands
              why (we are gathering real signals to build their brief). */}
          {(showGathering || showBeat) && (
            <div className="px-6 pt-5">
              <GatheringSignals
                competitor={currentCompetitor}
                agents={state.agents}
                isComplete={showBeat}
                signals={signals}
              />
            </div>
          )}

          {/* Agent swarm panel - the detailed, real-time per-specialist view.
              Sits below the friendly hero so power users still get the full feed. */}
          <div className="px-6 pt-5">
            <AgentSwarmPanel agents={state.agents} isVisible={state.runStatus !== "idle"} signals={signals} />
          </div>

          {/* Threat assessment - synthesis layer, above the brief.
              Renders a skeleton while running, the scored card once it arrives. */}
          {(state.threat || isRunning) && (
            <div className="px-6 pb-1">
              <ThreatPanel threat={state.threat} competitor={currentCompetitor} isRunning={isRunning} />
            </div>
          )}

          {/* Error */}
          {hasError && state.errorMessage && (
            <div className="mx-6 mb-4 flex items-start gap-3 bg-red-950/25 border border-red-800/35 rounded-xl p-4">
              <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium text-red-300">Analysis couldn't complete</p>
                <p className="text-xs text-red-500/80 mt-0.5">{state.errorMessage}</p>
              </div>
            </div>
          )}

          {/* Synthesis output */}
          {hasOutput && (
            <div className="relative overflow-hidden px-6 pb-5 max-h-[62vh] overflow-y-auto scrollbar-thin">
              <div
                className="absolute inset-0 pointer-events-none opacity-[0.025] z-10"
                style={{
                  background: "repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,1) 2px, rgba(0,0,0,1) 4px)",
                }}
              />
              <div className="relative">
                <MarkdownView variant="brief">{state.synthesisText}</MarkdownView>
                {isRunning && (
                  <span className="inline-block w-0.5 h-4 bg-[#22d3ee] ml-0.5 align-middle animate-stream-cursor" />
                )}
              </div>
              {/* Trust-leading source block under the synthesis brief, once the
                  run has settled and the threat synthesis has supplied sources. */}
              {!isRunning && state.threat && state.threat.sources.length > 0 && (
                <Sources urls={state.threat.sources} className="relative mt-5 pt-4 border-t border-white/10" />
              )}
              <div ref={outputEndRef} />
            </div>
          )}

          {/* Battlecard - battle-ready brief, below the synthesis. Collapsed by
              default; shows its own "Assembling…" skeleton while the run is live. */}
          {(state.battlecard || isRunning) && (
            <div className="px-6 pb-5 pt-1">
              <Battlecard battlecard={state.battlecard} competitor={currentCompetitor} isRunning={isRunning} />
            </div>
          )}

          {/* SEC 8-K filing alerts - subscribe this competitor to email + Slack
              alerts on new high-signal filings. Shown alongside the email/Slack
              delivery actions once a brief exists for a named competitor. */}
          {hasOutput && !isRunning && currentCompetitor && (
            <div className="px-6 pb-5 pt-1" data-print="hide">
              <AlertsToggle competitor={currentCompetitor} slackConnected={slackConnected} />
            </div>
          )}

          {/* Streaming footer */}
          {isRunning && (
            <div className="px-6 py-3.5 border-t border-[#1a2640] bg-[#091020]/40 flex items-center gap-3">
              <div className="flex items-end gap-0.5 h-4">
                {[0, 1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className="w-0.5 bg-brand-500 rounded-full animate-wave shadow-[0_0_4px_#22d3ee80]"
                    style={{
                      height: `${[8, 12, 16, 12, 8][i]}px`,
                      animationDelay: `${i * 100}ms`,
                      animationDuration: "1.2s",
                    }}
                  />
                ))}
              </div>
              <span className="text-xs text-slate-500">
                Specialists working in parallel - News · Documents · Web · Video
              </span>
            </div>
          )}
        </div>
      )}

    </div>
  );
}
